/** Tipos da agenda IA-API (Central Amparo). */

export type SupportCenterAgendaProfessional = {
  id: string
  name: string
  email: string
}

/** Slot interno (achatamento de GET /disponibilidade) usado pelo formulário e pela grade. */
export type SupportCenterAgendaSlot = {
  /** Data no formato dd/mm/aaaa (como na API) ou yyyy-mm-dd após normalização. */
  data: string
  /** Hora cheia "HH:00". */
  horario: string
  scheduled_at: string
}

/** Dia livre dentro de uma semana em GET /centralamparo/disponibilidade. */
export type SupportCenterAgendaDisponibilidadeData = {
  /** dd/mm/aaaa */
  data: string
  /** Horas inteiras livres (ex.: 8, 9, 19). */
  horas: number[]
}

/** Semana com datas livres em GET /centralamparo/disponibilidade. */
export type SupportCenterAgendaDisponibilidadeSemana = {
  semana: string
  datas: SupportCenterAgendaDisponibilidadeData[]
}

/** Item de `data.profissionais` em GET /centralamparo/disponibilidade. */
export type SupportCenterAgendaDisponibilidadeProfissional = {
  id: string
  name: string
  especialidade: string
  email: string
  slots: SupportCenterAgendaDisponibilidadeSemana[]
}

/** Canal escolhido no modal Novo agendamento / Reagendar. */
export type SupportCenterAgendaCreateChannel = 'whatsapp' | 'telefone' | 'videochamada'

/** Garante canal do POST /agendamentos: só whatsapp | telefone | videochamada. */
export function parseAgendaCreateCanal(
  value: string | null | undefined
): SupportCenterAgendaCreateChannel {
  const canal = String(value ?? '')
    .trim()
    .toLowerCase()
  if (canal === 'whatsapp' || canal === 'telefone' || canal === 'videochamada') {
    return canal
  }
  throw new Error(
    'Selecione o canal de agendamento (Telefone, WhatsApp ou Videochamada).'
  )
}

/** Situação do titular no POST /agendamentos/pre. */
export type SupportCenterAgendaTitularSituacao = 'ativo' | 'falecido'

/**
 * Contrato POST /agendamentos/pre — hold de horário (status pre_agendado).
 * Sem e-mail/telefone do paciente, triagem nem Outlook; API resolve elegibilidade/códigos.
 * Promoção: POST /agendamentos com id_pre_agendamento.
 */
export type SupportCenterAgendaPreBookingApiBody = {
  cpf_titular: string
  cpf_paciente: string
  titular_situacao: SupportCenterAgendaTitularSituacao
  nome_paciente: string
  data_nascimento_paciente: string
  relacionamento_com_titular?: string | null
  id_profissional: string
  emails_internos?: string[]
  especialidade_id: string
  codigo_assistencia: string
  data_agendada: string
  canal: SupportCenterAgendaCreateChannel
}

/**
 * Contrato POST /agendamentos e POST /agendamentos/{id}/reagendar.
 *
 * - `id_profissional`: UUID da pessoa (profiles / auth user).
 * - `especialidade_id`: UUID do catálogo (support_center_specialties.id).
 * - `canal`: valor selecionado no formulário (telefone, whatsapp ou videochamada).
 */
export type SupportCenterAgendaCreateApiBody = {
  cpf_titular: string
  cpf_paciente: string
  /** Nome do paciente (titular ou dependente). API materializa em people quando ausente. */
  nome_paciente: string
  /** Data de nascimento do paciente (yyyy-mm-dd). */
  data_nascimento_paciente: string
  relacionamento_com_titular?: string | null
  /** UUID da pessoa (profiles / auth user). */
  id_profissional: string
  emails_internos?: string[]
  /** Um único e-mail do paciente (string). A rota não aceita lista. */
  email_paciente: string
  telefone_paciente: string
  /** UUID do catálogo de especialidade. */
  especialidade_id: string
  /** Código de cobertura da assistência usada no agendamento. */
  codigo_assistencia: string
  data_agendada: string
  /** Apólice da cobertura elegível (GET /elegibilidade). */
  cod_apolice: number
  /** Convênio da cobertura elegível (GET /elegibilidade). */
  cod_convenio: number
  /** Fornecedor de assistência (null da elegibilidade é enviado como 0). */
  cod_fornecedor_assistencia: number
  /** Corretor da cobertura elegível (GET /elegibilidade). */
  cod_corretor: number
  /** Cliente da cobertura elegível (GET /elegibilidade). */
  cod_cliente: number
  /** Canal selecionado no modal (telefone, whatsapp ou videochamada). */
  canal: SupportCenterAgendaCreateChannel
  /**
   * Se informado: API promove o pré-agendamento (status pre_agendado → marcado)
   * em vez de INSERT. Ver doc/central-amparo-pre-agendamento-api.md.
   */
  id_pre_agendamento?: string
}

/** POST /agendamentos/{id}/cancelar — cancela via IA-API. */
export type SupportCenterAgendaCancelChannel = 'whatsapp' | 'telefone' | 'videochamada'

/** POST /agendamentos/{id}/reagendar — mesmo body do create + cancelamento do antigo. */
export type SupportCenterAgendaRescheduleApiBody = SupportCenterAgendaCreateApiBody & {
  excluido_por: string
  id_motivo_cancelamento: string
  canal_cancelamento: SupportCenterAgendaCancelChannel
}

export type SupportCenterAgendaCancelInput = {
  excluido_por: string
  id_motivo_cancelamento: string
  status: 'cancelado'
  canal_cancelamento: SupportCenterAgendaCancelChannel
}

export type SupportCenterElegibilidadePessoa = {
  name: string | null
  tax_id_number: string | null
  captured_birth_date: string | null
}

/** Cobertura elegível (origem CodCobertura / DscCobertura da IA-API). */
export type SupportCenterElegibilidadeCobertura = {
  code: string
  name: string
  codApolice: number | null
  codConvenio: number | null
  codFornecedorAssistencia: number | null
  codCorretor: number | null
  codCliente: number | null
}

/**
 * Item do catálogo elegível (GET /elegibilidade).
 * Cada item traz coberturas e pessoas (`profissionais`) já embutidas.
 */
export type SupportCenterElegibilidadeEspecialidade = {
  id: string
  name: string
  form_kind: string | null
  coberturas: SupportCenterElegibilidadeCobertura[]
  profissionais: SupportCenterAgendaProfessional[]
}

/** @deprecated Use SupportCenterElegibilidadeEspecialidade */
export type SupportCenterElegibilidadeProfissional = SupportCenterElegibilidadeEspecialidade

/**
 * Contrato GET /elegibilidade:
 * - `especialidades` / `especialidades_elegiveis`: catálogo elegível
 */
export type SupportCenterElegibilidadeResult = {
  pessoa: SupportCenterElegibilidadePessoa
  especialidadeIds: string[]
  especialidades: SupportCenterElegibilidadeEspecialidade[]
}

/** Resposta mapeada da edge `buscar-pessoa-cpf` (ausente → Error). */
export type IaApiPessoaCpf = {
  name: string | null
  tax_id_number: string | null
  captured_birth_date: string | null
}
